<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useVirtualKeyboard } from '@/composables/keyboard'
import { $bus } from '@/common/events'

const { vkeyboard, keyboardType } = useVirtualKeyboard();

const props = defineProps({
  id: {
    type: String,
    default: null
  },
  name: {
    type: String,
    default: null
  },
  type: {
    type: String,
    default: null,
  },
  placeholder: {
    type: String,
    default: ""
  },
  enabled: {
    type: Boolean,
    default: true
  },
  required: {
    type: Boolean,
    default: false
  },
  readonly: {
    type: Boolean,
    default: false
  },
  masked: {
    type: Boolean,
    default: false
  },
  grid: {
    type: Boolean,
    default: false
  },
  validation: {
    type: String,
    default: null
  },
  modelValue: {
  }
});

const emit = defineEmits([ "update:modelValue", "change", "enter" ]);

const focus = (e) => { };
const unfocus = (e) => { };
defineExpose({ focus, unfocus });

const type = computed(() => props.masked == true ? "password" : "text");

const value = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    emit('update:modelValue', value);
    emit('change', value);
  }
});

const textbox = ref(null);
const isFocusForKeyboard = ref(false);

function handleKeyboardHidden() {
  isFocusForKeyboard.value = false;
}

onMounted(() => {
  $bus.on("keyboard hidden", handleKeyboardHidden);
  vkeyboard.on("target change", onTargetChange); //Keyboard focus
});

onUnmounted(() => {
  $bus.off("keyboard hidden", handleKeyboardHidden);
  vkeyboard.removeListener("target change", onTargetChange); //Keyboard unfocus
});

function onTargetChange(target) {
  if (target == textbox.value) {
    isFocusForKeyboard.value = true;
    vkeyboard.removeAllListeners("enter");
    vkeyboard.on("enter", onEnterKey);
  }
  else {
    isFocusForKeyboard.value = false;
    vkeyboard.removeListener("enter", onEnterKey);
  }
}

function onFocus(e) {
  if (props.enabled && !props.readonly) {
    keyboardType.value = props.type == "numeric" ? "numeric" : "default";
    focus(e);
    vkeyboard.show(e.target);
  }
}

function onUnfocus(e) {
  if (props.enabled && !props.readonly) {
    unfocus(e);
    vkeyboard.hide(e.target, { delay: 120 }); //Give other input a chance to cancel hide within 30ms
  }
}

function onEnterKey() {
  emit("enter");
}
</script>

<template>
  <!-- autocomplete="new-password" -->
  <!-- :class="$attrs.class" -->
  <input :type="type" :id="props.id" ref="textbox" v-model="value" :placeholder="props.placeholder"
    :required="!!props.required" :readonly="!!props.readonly" :disabled="!props.enabled"
    role="presentation" autocomplete="new-password"
    class="" :class="[ { 'textbox': !props.grid, 'textbox-grid': !!props.grid, 'textbox-error': !!props.validation, 'focus-for-keyboard': isFocusForKeyboard }, $attrs.class ]"
    @focus="onFocus" @blur="onUnfocus" @keypress.enter="onEnterKey" />
  <!-- <div v-if="props.validation" class="text-red-800 text-sm mt-2">{{ props.validation }}</div> -->
  <div class="textbox-validation-message" :class="$attrs.class" v-if="!!props.validation">
    {{ typeof validation == "string" ? validation : validation.message }}
  </div>
</template>

<style scoped>
.textbox {
  @apply bg-white border border-gray-300 text-gray-900 text-sm rounded-lg block p-2.5
    focus:ring-blue-500 focus:border-blue-500
    dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500;
}

.textbox-grid {
  @apply bg-white border-l border-r border-gray-300 text-gray-900 text-sm block p-2.5
    focus:ring-blue-500 focus:border-blue-500
    dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500;
}

.textbox:disabled,
.textbox-grid:disabled {
  @apply text-gray-400;
}

.textbox-error {
  @apply border-red-700 bg-red-100 text-red-900 focus:ring-red-500 focus:border-red-500;
}

.items-group .textbox {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  border-right: 0;
}

input[type="text"].textbox:focus,
input[type="text"].textbox.focus-for-keyboard {
  @apply ring-blue-500 border-blue-500;

  outline: 2px solid transparent;
  outline-offset: 2px;
  --tw-ring-inset: var(--tw-empty, );
  --tw-ring-offset-width: 0px;
  --tw-ring-offset-color: #fff;
  --tw-ring-color: #1C64F2;
  --tw-ring-offset-shadow: var(--tw-ring-inset) 0 0 0 var(--tw-ring-offset-width) var(--tw-ring-offset-color);
  --tw-ring-shadow: var(--tw-ring-inset) 0 0 0 calc(1px + var(--tw-ring-offset-width)) var(--tw-ring-color);
  box-shadow: var(--tw-ring-offset-shadow), var(--tw-ring-shadow), var(--tw-shadow);
  border-color: #1C64F2;
}
</style>